import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { fetchCart } = useCart();

  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      if (pollAttempts >= 10) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails(response.data);
          // Refresh cart to clear items
          fetchCart();
          return;
        }

        if (response.data.status === 'expired') {
          setStatus('error');
          return;
        }

        // Continue polling
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPollAttempts(prev => prev + 1);
        if (pollAttempts < 10) {
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollAttempts, fetchCart]);

  if (status === 'loading') {
    return (
      <div className="pt-20 min-h-screen bg-sand flex items-center justify-center" data-testid="checkout-loading">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-bush mx-auto mb-6" />
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-2">Processing Your Order</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pt-20 min-h-screen bg-sand flex items-center justify-center" data-testid="checkout-error">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-4">Payment Issue</h2>
          <p className="text-gray-600 mb-8">
            We couldn't confirm your payment. If you believe this is an error, please contact our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cart">
              <Button variant="outline" className="rounded-full px-6">
                Return to Cart
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-bush hover:bg-bush-dark text-white rounded-full px-6">
                Go to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-sand" data-testid="checkout-success">
      <div className="max-w-2xl mx-auto px-4 py-24">
        <div className="bg-white rounded-3xl p-12 shadow-card text-center">
          <div className="w-24 h-24 bg-bush/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="h-12 w-12 text-bush" />
          </div>
          
          <h1 className="text-3xl font-serif font-bold text-[#1A1A1A] mb-4" data-testid="success-title">
            Thank You for Your Order!
          </h1>
          
          <p className="text-gray-600 text-lg mb-8">
            Your custom cushion order has been confirmed. We'll start crafting your cushions right away.
          </p>

          {orderDetails && (
            <div className="bg-sand rounded-2xl p-6 mb-8 text-left">
              <div className="flex items-center gap-3 mb-4">
                <Package className="h-6 w-6 text-bush" />
                <span className="font-semibold text-lg">Order Details</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID</span>
                  <span className="font-medium">{sessionId?.slice(0, 16)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid</span>
                  <span className="font-bold text-bush">
                    ${(orderDetails.amount_total / 100).toFixed(0)} {orderDetails.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="text-bush font-medium">Confirmed</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              A confirmation email has been sent to your email address with all the order details.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link to="/products">
                <Button className="bg-bush hover:bg-bush-dark text-white rounded-full px-8 py-6" data-testid="continue-shopping-btn">
                  Continue Shopping
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
          <div className="p-6">
            <div className="text-3xl mb-2">📦</div>
            <h3 className="font-semibold mb-1">Production Time</h3>
            <p className="text-sm text-gray-600">10-14 business days</p>
          </div>
          <div className="p-6">
            <div className="text-3xl mb-2">🚚</div>
            <h3 className="font-semibold mb-1">Free Shipping</h3>
            <p className="text-sm text-gray-600">On orders over $400</p>
          </div>
          <div className="p-6">
            <div className="text-3xl mb-2">✉️</div>
            <h3 className="font-semibold mb-1">Order Updates</h3>
            <p className="text-sm text-gray-600">Via email notifications</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
